import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProfilePage from './pages/ProfilePage';
import Notification from './components/Notification';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CenterProvider } from './contexts/CenterContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import { readAuthTokens } from './services/clientAuthStorage';
import { securityService } from './services/securityService';
import { msUntilRefresh } from './utils/authTokenUtils';
import PreferencesPage from './pages/PreferencesPage';
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import RequirePermission from './components/RequirePermission';
import RolesPage from './pages/RolesPage';
import PermissionsPage from './pages/PermissionsPage';
import PermissionFormPage from './pages/PermissionFormPage';
import PermissionDetailPage from './pages/PermissionDetailPage';
import UsersPage from './pages/UsersPage';
import UserDetailPage from './pages/UserDetailPage';
import UserFormPage from './pages/UserFormPage';
import UserManagementPage from './pages/UserManagementPage';
import RoleManagementPage from './pages/RoleManagementPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationFormPage from './pages/ApplicationFormPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import RoleTemplatesPage from './pages/RoleTemplatesPage';
import RoleTemplateFormPage from './pages/RoleTemplateFormPage';
import RoleTemplateDetailPage from './pages/RoleTemplateDetailPage';
import ClientsPage from './pages/ClientsPage';
import ClientFormPage from './pages/ClientFormPage';
import ClientDetailPage from './pages/ClientDetailPage';
import SucursalesPage from './pages/SucursalesPage';
import SucursalDetailPage from './pages/SucursalDetailPage';
import SucursalFormPage from './pages/SucursalFormPage';
import TipoElementosPage from './pages/TipoElementosPage';
import TipoElementoFormPage from './pages/TipoElementoFormPage';
import TipoElementoDetailPage from './pages/TipoElementoDetailPage';
import ExtintoresPage from './pages/ExtintoresPage';
import ExtintorFormPage from './pages/ExtintorFormPage';
import ExtintorDetailPage from './pages/ExtintorDetailPage';
import ElementosPage from './pages/ElementosPage';
import ElementoFormPage from './pages/ElementoFormPage';
import ElementoDetailPage from './pages/ElementoDetailPage';
import PuestosPage from './pages/PuestosPage';
import PuestoFormPage from './pages/PuestoFormPage';
import PuestoDetailPage from './pages/PuestoDetailPage';
import CheckListsPage from './pages/CheckListsPage';
import CheckListFormPage from './pages/CheckListFormPage';
import CheckListDetailPage from './pages/CheckListDetailPage';
import PresupuestosPage from './pages/PresupuestosPage';
import PresupuestoFormPage from './pages/PresupuestoFormPage';
import PresupuestoDetailPage from './pages/PresupuestoDetailPage';
import RemitoUsuariosPage from './pages/RemitoUsuariosPage';
import RemitoUsuarioFormPage from './pages/RemitoUsuarioFormPage';
import RemitoUsuarioDetailPage from './pages/RemitoUsuarioDetailPage';
import RemitosPage from './pages/RemitosPage';
import RemitoDetailPage from './pages/RemitoDetailPage';
import TareasPage from './pages/TareasPage';
import TareaFormPage from './pages/TareaFormPage';
import TareaDetailPage from './pages/TareaDetailPage';
import SolicitudesPage from './pages/SolicitudesPage';
import SolicitudDetailPage from './pages/SolicitudDetailPage';
import RelevamientosPage from './pages/RelevamientosPage';
import RelevamientoDetailPage from './pages/RelevamientoDetailPage';
import NotificationsPage from './pages/NotificationsPage';
import PuestosRelevadosReportPage from './pages/PuestosRelevadosReportPage';
import ElementosRelevadosReportPage from './pages/ElementosRelevadosReportPage';
import QRsPage from './pages/QRsPage';
import RemitoFormPage from './pages/RemitoFormPage';
import CentersPage from './pages/CentersPage';
import CenterDetailPage from './pages/CenterDetailPage';
import CenterFormPage from './pages/CenterFormPage';
import CalendarPage from './pages/CalendarPage';
import AgendaContactosPage from './pages/AgendaContactosPage';
import ContactosPage from './pages/ContactosPage';
import ContactoFormPage from './pages/ContactoFormPage';
import AppAccessPage from './pages/AppAccessPage';
import OrdenesTrabajoPage from './pages/OrdenesTrabajoPage';
import OrdenTrabajoFormPage from './pages/OrdenTrabajoFormPage';
import OrdenTrabajoDetailPage from './pages/OrdenTrabajoDetailPage';
import SecurityDashboardPage from './pages/SecurityDashboardPage';
import AppsDownloadPage from './pages/AppsDownloadPage';
import PreferencesLayout from './pages/PreferencesLayout';

/** Evita mostrar shell privado hasta que /profile haya hidratado usuario y alcances. */
function PrivateAppShell({ children }: { children: React.ReactNode }) {
  const { isLoading } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300">
        Cargando sesión…
      </div>
    );
  }
  return <>{children}</>;
}

const PrivateRoute: React.FC<{
  children: React.ReactNode;
  onExpire: () => void;
  sessionActive: boolean;
}> = ({ children, onExpire, sessionActive }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionActive) return;
    if (!securityService.hasStoredSession()) {
      onExpire();
      navigate('/login');
    }
  }, [sessionActive, onExpire, navigate]);

  return sessionActive ? <>{children}</> : <Navigate to="/login" replace />;
};

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [sessionActive, setSessionActive] = useState(() => securityService.hasStoredSession());
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [darkMode] = useState(false);

  const syncSessionFromStorage = () => {
    setToken(localStorage.getItem('token'));
    setSessionActive(securityService.hasStoredSession());
  };

  useLayoutEffect(() => {
    window.addEventListener('incer-client-auth-cleared', syncSessionFromStorage);
    return () => window.removeEventListener('incer-client-auth-cleared', syncSessionFromStorage);
  }, []);

  useEffect(() => {
    if (!sessionActive) return;

    const scheduleRefresh = () => {
      const { accessToken, expiresAt } = readAuthTokens();
      const delay = msUntilRefresh(expiresAt, accessToken);
      if (delay == null) return undefined;
      return window.setTimeout(async () => {
        try {
          const ok = await securityService.refreshSession();
          if (ok) {
            syncSessionFromStorage();
            setNotification({ message: 'Sesión renovada automáticamente.', type: 'info' });
          }
        } catch {
          setNotification({ message: 'No se pudo renovar la sesión.', type: 'error' });
        }
      }, delay);
    };

    const timeoutId = scheduleRefresh();
    return () => {
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [token, sessionActive]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && sessionActive) {
        void securityService.getValidAccessToken().then((t) => {
          if (t) syncSessionFromStorage();
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [sessionActive]);

  const handleLogin = () => {
    syncSessionFromStorage();
    setNotification({ message: '¡Bienvenido!', type: 'success' });
  };

  const handleLogout = async () => {
    try {
      await securityService.logout();
      setToken(null);
      setSessionActive(false);
      setNotification({ message: 'Sesión cerrada.', type: 'info' });
    } catch {
      setNotification({ message: 'No se pudo cerrar sesión completamente.', type: 'error' });
    }
  };

  const handleExpire = () => {
    setNotification({
      message: 'La sesión ha expirado. Por favor, inicia sesión nuevamente.',
      type: 'error',
    });
    setToken(null);
    setSessionActive(false);
  };

  // Dark mode is controlled via PreferencesPage and index.tsx

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const username = user.alias || user.email || 'User';

  return (
    <AuthProvider>
      <CenterProvider>
        <PermissionsProvider>
          <Router>
            {!sessionActive ? (
              <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                <Routes>
                  <Route
                    path="/login"
                    element={<LoginPage onLogin={handleLogin} darkMode={darkMode} />}
                  />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </Routes>
              </div>
            ) : (
              <PrivateAppShell>
                <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
                  {/* Sidebar */}
                  <Sidebar />

                  {/* Main Content */}
                  <div className="flex-1 flex flex-col overflow-hidden">
                    {/* Topbar */}
                    <Topbar
                      username={username}
                      onLogout={handleLogout}
                      darkMode={false}
                      onToggleDarkMode={() => {}}
                    />

                    {/* Main Content Area */}
                    <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900">
                      <div className="container mx-auto px-6 py-8">
                        <Routes>
                          <Route
                            path="/apps"
                            element={<Navigate to="/preferences/apps" replace />}
                          />
                          <Route
                            path="/dashboard"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RequirePermission
                                  anyOf={[
                                    { resource: 'dashboard', action: 'read' },
                                    { resource: 'dashboard', action: 'list' },
                                  ]}
                                >
                                  <DashboardPage />
                                </RequirePermission>
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/perfil"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ProfilePage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/preferences"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PreferencesLayout />
                              </PrivateRoute>
                            }
                          >
                            <Route index element={<PreferencesPage />} />
                            <Route path="apps" element={<AppsDownloadPage />} />
                          </Route>
                          {/* Gestión Integrada de Seguridad */}
                          <Route
                            path="/user-management"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <UserManagementPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/role-management"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RoleManagementPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/permissions"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PermissionsPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/permissions/edit/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PermissionFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/permissions/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PermissionDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/applications"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ApplicationsPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/applications/new"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ApplicationFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/applications/edit/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ApplicationFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/applications/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ApplicationDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/role-templates"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RoleTemplatesPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/roletemplates"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RoleTemplatesPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/roletemplates/new"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RoleTemplateFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/roletemplates/edit/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RoleTemplateFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/roletemplates/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RoleTemplateDetailPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Rutas legacy - mantener para compatibilidad */}
                          <Route
                            path="/roles"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RolesPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/users"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <UsersPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/users/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <UserFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/users/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <UserDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/users/edit/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <UserFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clientes"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ClientsPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clientes/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ClientFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clientes/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ClientDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/clientes/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ClientFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/agenda-contactos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <AgendaContactosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/contactos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ContactosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/contactos/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ContactoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/contactos/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ContactoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/contactos/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ContactoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/app-access"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <AppAccessPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/security-matrix"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RequirePermission
                                  anyOf={[
                                    { resource: 'access', action: 'list' },
                                    { resource: 'access', action: 'manage' },
                                  ]}
                                >
                                  <SecurityDashboardPage />
                                </RequirePermission>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/sucursales"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <SucursalesPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/sucursales/create"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <SucursalFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/sucursales/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <SucursalDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/sucursales/edit/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <SucursalFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/centers"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CentersPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/centers/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CenterFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/centers/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CenterDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/centers/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CenterFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tipos-elemento"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TipoElementosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tipos-elemento/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TipoElementoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tipos-elemento/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TipoElementoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tipos-elemento/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TipoElementoFormPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Checklists */}
                          <Route
                            path="/checklists"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CheckListsPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/checklists/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CheckListFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/checklists/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CheckListDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/checklists/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CheckListFormPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Presupuestos */}
                          <Route
                            path="/presupuestos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PresupuestosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/presupuestos/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PresupuestoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/presupuestos/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PresupuestoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/presupuestos/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PresupuestoFormPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Asignación de Remitos */}
                          <Route
                            path="/remito-usuarios"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitoUsuariosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/remito-usuarios/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitoUsuarioFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/remito-usuarios/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitoUsuarioDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/remito-usuarios/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitoUsuarioFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/remitos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/remitos/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/remitos/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RemitoFormPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Gestión */}
                          <Route
                            path="/calendario"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <CalendarPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tareas"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TareasPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tareas/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TareaFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tareas/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TareaDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/tareas/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <TareaFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/solicitudes"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <SolicitudesPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/solicitudes/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <SolicitudDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/relevamientos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RelevamientosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/relevamientos/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RelevamientoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/notificaciones"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <NotificationsPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/qrs"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <QRsPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Reportes */}
                          <Route
                            path="/reportes/puestos-relevados"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RequirePermission
                                  anyOf={[
                                    { resource: 'reportes.puestos-relevados', action: 'read' },
                                    { resource: 'reportes', action: 'read' },
                                  ]}
                                >
                                  <PuestosRelevadosReportPage />
                                </RequirePermission>
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/reportes/elementos-relevados"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <RequirePermission
                                  anyOf={[
                                    { resource: 'reportes.elementos-relevados', action: 'read' },
                                    { resource: 'reportes', action: 'read' },
                                  ]}
                                >
                                  <ElementosRelevadosReportPage />
                                </RequirePermission>
                              </PrivateRoute>
                            }
                          />

                          {/* Producción */}
                          <Route
                            path="/ordenes-trabajo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <OrdenesTrabajoPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/ordenes-trabajo/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <OrdenTrabajoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/ordenes-trabajo/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <OrdenTrabajoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/ordenes-trabajo/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <OrdenTrabajoFormPage />
                              </PrivateRoute>
                            }
                          />

                          {/* Productos */}
                          <Route
                            path="/extintores"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ExtintoresPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/extintores/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ExtintorFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/extintores/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ExtintorDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/extintores/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ExtintorFormPage />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/elementos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ElementosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/elementos/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ElementoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/elementos/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ElementoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/elementos/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <ElementoFormPage />
                              </PrivateRoute>
                            }
                          />

                          <Route
                            path="/puestos"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PuestosPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/puestos/nuevo"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PuestoFormPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/puestos/:id"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PuestoDetailPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/puestos/:id/editar"
                            element={
                              <PrivateRoute onExpire={handleExpire} sessionActive={sessionActive}>
                                <PuestoFormPage />
                              </PrivateRoute>
                            }
                          />

                          <Route path="*" element={<Navigate to="/dashboard" replace />} />
                        </Routes>
                      </div>
                    </main>
                  </div>

                  {/* Notification */}
                  <Notification
                    message={notification?.message || ''}
                    type={notification?.type}
                    onClose={() => setNotification(null)}
                  />
                </div>
              </PrivateAppShell>
            )}
          </Router>
        </PermissionsProvider>
      </CenterProvider>
    </AuthProvider>
  );
}

export default App;
